import React, { useState, useEffect } from "react";
import { Box, Card, CardContent, CardHeader, Typography, Grid, Avatar, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert, Divider, Stack, IconButton, Tooltip, Button, useTheme, alpha, Checkbox } from "@mui/material";
import {
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Gavel as GavelIcon,
  DirectionsCar as DirectionsCarIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarTodayIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  RestartAlt as RestartAltIcon,
  Block as BlockIcon,
  LockOpen as UnblockIcon,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { capitalize, capitalizeFirstLetter } from "../../utils/helpers";
import { fDateTime } from "../../utils/formatTime";
import { Loader } from "../../components/CustomLoader";
import BackButton from "../../components/Buttons/BackButton";
import { useSocket } from "../../services/SocketProvider";
import { IMAGE_BASE } from "../../utils/constants";
import { toast } from "react-toastify";
import errorHandler from "../../utils/errorHandler";
import apiAuctions from "../../services/auctionService";
import Swal from "sweetalert2";

const ViewBidControl = () => {
  const { id, type } = useParams();
  const { socketEmit, socketOn, isConnected } = useSocket();

  const [auction, setAuction] = useState(null);
  const [bids, setBids] = useState([]);
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [priceDrops, setPriceDrops] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);

  const getAuctionTypeFromUrl = () => {
    if (type === "market-dropai") return "marketDropAi";
    if (type === "claimpoint") return "claimPoint";
    if (type === "auction") return "normal";
    return null;
  };

  const auctionTypeFilter = getAuctionTypeFromUrl();

  useEffect(() => {
    if (id && isConnected) {
      socketEmit("admin_get_bid_history", {
        auctionId: id
      });
    }
  }, [id, isConnected, socketEmit]);

  useEffect(() => {
    if (!socketOn) return;

    const cleanup = socketOn("admin_bid_history_response", (response) => {
    try {
      setLoading(true);
      setError(null);
        const responseData = response?.data;
        
        if (!responseData) {
          console.warn("No data in socket response");
          setLoading(false);
          return;
        }

        if (responseData.auction) {
          setAuction({
            ...responseData.auction,
            vehicle: responseData.vehicle || responseData.auction.vehicle,
          });
        }

        const bidsArray = responseData.allBids || [];
      let allBids = [];

        if (Array.isArray(bidsArray) && bidsArray.length > 0) {
          allBids = bidsArray.map((bid) => {
            const mappedBid = {
          ...bid,
              amount: bid.bidAmount || bid.amount, 
              status: bid.approved === true ? "approved" : bid.approved === false ? "rejected" : bid.status || "pending",
              createdAt: bid.placedAt || bid.createdAt || bid.created_at || bid.timestamp || bid.date || new Date().toISOString(),
              depositVerified: bid.depositVerified || false,
              bidder: bid.bidder ? {
                ...bid.bidder,
                name: bid.bidder.name || 
                      (bid.bidder.firstName && bid.bidder.lastName 
                        ? `${bid.bidder.firstName} ${bid.bidder.lastName}`.trim()
                        : bid.bidder.firstName || "Unknown"),
                email: bid.bidder.email || "",
                phone: bid.bidder.phone || "",
                userImage: bid.bidder.userImage || null,
              } : {
                _id: "",
                name: "Unknown",
                email: "",
                phone: "",
                userImage: null,
              },
              auction: responseData.auction || {},
            };
            return mappedBid;
          });

          allBids.sort((a, b) => (b.amount || 0) - (a.amount || 0));

          if (auctionTypeFilter === "claimPoint") {
            setBids(allBids);
            
            if (responseData.claimPointData && Array.isArray(responseData.claimPointData) && responseData.claimPointData.length > 0) {
              const mappedClaimPointData = responseData.claimPointData.map((bid) => {
                const mappedBid = {
                  ...bid,
                  amount: bid.bidAmount || bid.maxClaimPrice || bid.amount || 0,
                  maxClaimPrice: bid.maxClaimPrice || bid.bidAmount || bid.amount || 0,
                  status: bid.approved === true ? "approved" : bid.approved === false ? "rejected" : bid.status || "pending",
                  createdAt: bid.placedAt || bid.createdAt || bid.created_at || bid.timestamp || bid.date || new Date().toISOString(),
                  depositVerified: bid.depositVerified || false,
                  bidder: bid.bidder ? {
                    ...bid.bidder,
                    name: bid.bidder.name || 
                          (bid.bidder.firstName && bid.bidder.lastName 
                            ? `${bid.bidder.firstName} ${bid.bidder.lastName}`.trim()
                            : bid.bidder.firstName || "Unknown"),
                    email: bid.bidder.email || "",
                    phone: bid.bidder.phone || "",
                    userImage: bid.bidder.userImage || null,
                  } : {
                    _id: "",
                    name: "Unknown",
                    email: "",
                    phone: "",
                    userImage: null,
                  },
                  auction: responseData.auction || {},
                };
                return mappedBid;
              });
              
              mappedClaimPointData.sort((a, b) => (b.amount || 0) - (a.amount || 0));
              setPriceDrops(mappedClaimPointData);
            } else {
              if (responseData.priceDrops && Array.isArray(responseData.priceDrops)) {
                setPriceDrops(responseData.priceDrops);
              } else {
                setPriceDrops([]);
              }
            }
            
            if (responseData.winner && responseData.winner._id) {
              const winnerId = responseData.winner._id;
              const winnerBid = allBids.find((bid) => 
                bid.bidder?._id === winnerId || bid.userId === winnerId
              );
              if (winnerBid) {
                winnerBid.isWinner = true;
                setWinners([winnerBid]);
              } else {
                setWinners([]);
              }
            } else {
              setWinners([]);
            }
            
            setLoading(false);
            return;
          }

        if (auctionTypeFilter === "marketDropAi") {
            const highestBid = allBids[0]; 
          if (highestBid) {
            highestBid.isWinner = true;
            setWinners([highestBid]);
          }
          } else if (responseData.winner) {        
            const winnerId = responseData.winner._id;
            allBids.forEach((bid) => {
              if (bid.bidder?._id === winnerId || bid.userId === winnerId) {
                bid.isWinner = true;
                setWinners([bid]);
              }
            });
          }
        }

        if (allBids.length > 0) {
      setBids(allBids);
        } else {
          console.warn("No bids found in socket response");
        }
      setLoading(false);
    } catch (error) {
        console.error("Error processing socket bid history:", error);
        setError("Failed to process bid history data");
        setLoading(false);
      }
    });

    return cleanup;
  }, [socketOn, auctionTypeFilter]);

  useEffect(() => {
    if (!socketOn) return;

    const cleanup = socketOn("admin_bid_placed", (newBidData) => {
      try {
        const mappedBid = {
          ...newBidData,
          amount: newBidData.bidAmount || (auctionTypeFilter === "claimPoint" ? newBidData.maxClaimPrice : null) || newBidData.amount || 0,
          maxClaimPrice: newBidData.maxClaimPrice || newBidData.bidAmount || newBidData.amount || 0,
          status: newBidData.approved === true ? "approved" : newBidData.approved === false ? "rejected" : newBidData.status || "pending",
          createdAt: newBidData.placedAt || newBidData.createdAt || newBidData.created_at || newBidData.timestamp || new Date().toISOString(),
          depositVerified: newBidData.depositVerified || false,
          depositAmount: newBidData.depositAmount || null,
          bidder: newBidData.bidder ? {
            ...newBidData.bidder,
            name: newBidData.bidder.name || 
                  (newBidData.bidder.firstName && newBidData.bidder.lastName 
                    ? `${newBidData.bidder.firstName} ${newBidData.bidder.lastName}`.trim()
                    : newBidData.bidder.firstName || "Unknown"),
            email: newBidData.bidder.email || "",
            phone: newBidData.bidder.phone || "",
            userImage: newBidData.bidder.userImage || null,
          } : {
            _id: "",
            name: "Unknown",
            email: "",
            phone: "",
            userImage: null,
          },
          auction: auction || {},
        };

        setBids((prevBids) => {
          const bidExists = prevBids.some(bid => bid._id === mappedBid._id);
          if (bidExists) {
            return prevBids;
          }

          const updatedBids = [...prevBids, mappedBid];
          updatedBids.sort((a, b) => (b.amount || 0) - (a.amount || 0));

          if (auctionTypeFilter === "marketDropAi") {
            const highestBid = updatedBids[0];
            if (highestBid) {
              // Clear previous winner flags
              updatedBids.forEach(bid => bid.isWinner = false);
              highestBid.isWinner = true;
              setWinners([highestBid]);
            }
          }
      
          return updatedBids;
        });

        if (auctionTypeFilter === "claimPoint" && mappedBid.maxClaimPrice) {
          setPriceDrops((prevPriceDrops) => {
            const priceDropExists = prevPriceDrops.some(drop => drop._id === mappedBid._id);
            if (priceDropExists) {
              return prevPriceDrops;
            }

            const updatedPriceDrops = [...prevPriceDrops, mappedBid];
            updatedPriceDrops.sort((a, b) => (b.maxClaimPrice || b.amount || 0) - (a.maxClaimPrice || a.amount || 0));
            return updatedPriceDrops;
          });
        }
      } catch (error) {
        console.error("Error processing new bid:", error);
      }
    });

    return cleanup;
  }, [socketOn, auctionTypeFilter, auction]);

  useEffect(() => {
    if (!socketOn || !id) return;

    const cleanupPaused = socketOn("auction_paused", (data) => {
      if (data?.auctionId === id || data?.id === id || data?._id === id) {
        setAuction((prev) => ({
          ...prev,
          auctionStatus: "Paused",
        }));
      }
    });

    const cleanupCompleted = socketOn("auction_completed", (data) => {
      if (data?.auctionId === id || data?.id === id || data?._id === id) {
        setAuction((prev) => ({
          ...prev,
          auctionStatus: "Completed",
        }));
        toast.success("Auction has been completed", {toastId: "auction-completed"});
      }
    });

    const cleanupResumed = socketOn("auction_resumed", (data) => {
      if (data?.auctionId === id || data?.id === id || data?._id === id) {
        setAuction((prev) => ({
          ...prev,
          auctionStatus: "Live",
        }));
      }
    });

    return () => {
      cleanupPaused();
      cleanupCompleted();
      cleanupResumed();
    };
  }, [socketOn, id]);

  // Common function to refresh blocked users
  const refreshBlockedUsers = async () => {
    if (!id) return;
    try {
      const response = await apiAuctions.getBlockedUsers({ auctionId: id });
      if (response?.data?.data?.blockedUsers) {
        const blockedIds = response?.data?.data?.blockedUsers
          .map(item => item.user?._id)
          .filter(Boolean);
        setBlockedUsers(blockedIds);
      }
    } catch (error) {
      console.error("Failed to fetch blocked users:", error);
    }
  };

  useEffect(() => {
    refreshBlockedUsers();
  }, [id]);

  // Checkbox handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Only select non-blocked users
      const allUserIds = bids
        .map(bid => bid.bidder?._id)
        .filter(userId => userId && !blockedUsers.includes(userId));
      setSelectedUsers(allUserIds);
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Common block handler for single or multiple users
  const handleBlockUsers = async (userIds) => {
    if (!userIds || userIds.length === 0 || !id) {
      toast.error("Please select at least one user");
      return;
    }

    const isMultiple = userIds.length > 1;
    const result = await Swal.fire({
      title: isMultiple ? "Block Users" : "Block User",
      text: isMultiple 
        ? `Are you sure you want to block ${userIds.length} user(s)?`
        : "Please enter a reason for blocking this user:",
      input: "textarea",
      inputPlaceholder: "Enter reason for blocking...",
      inputAttributes: {
        rows: 4
      },
      showCancelButton: true,
      confirmButtonText: "Block",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#6c757d",
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return "Please enter a reason";
        }
      }
    });

    if (result.isConfirmed && result.value) {
      try {
        setActionLoading(true);
        await apiAuctions.blockUser({
          auctionId: id,
          userId: userIds,
          reason: result.value.trim()
        });
        
        toast.success(isMultiple 
          ? `${userIds.length} user(s) blocked successfully`
          : "User blocked successfully"
        );
        
        if (isMultiple) {
          setSelectedUsers([]);
        }
        
        // Refresh blocked users list
        await refreshBlockedUsers();
      } catch (error) {
        errorHandler(error);
        toast.error("Failed to block user(s)");
      } finally {
        setActionLoading(false);
      }
    }
  };

  // Common unblock handler
  const handleUnblockUser = async (userId) => {
    if (!userId || !id) return;

    const result = await Swal.fire({
      title: "Unblock User",
      text: "Are you sure you want to unblock this user?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Unblock",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#2e7d32",
      cancelButtonColor: "#6c757d"
    });

    if (result.isConfirmed) {
      try {
        setActionLoading(true);
        await apiAuctions.unblockUser({
          auctionId: id,
          userId: [userId]
        });
        
        toast.success("User unblocked successfully");
        
        // Refresh blocked users list
        await refreshBlockedUsers();
      } catch (error) {
        errorHandler(error);
        toast.error("Failed to unblock user");
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleStartAuction = async () => {
    if (!id) return;
    try {
      setActionLoading(true);
      const response = await apiAuctions.makeAuctionLiveNow({ id });
      toast.success("Auction started successfully", {toastId: "auction-started"});
      setAuction((prev) => ({
        ...(response?.data?.auction || prev),
        auctionStatus: "Live",
      }));
    } catch (error) {
      errorHandler(error);
      toast.error("Failed to start auction");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePauseAuction = async () => {
    if (!id) return;
    
    const result = await Swal.fire({
      title: "Pause Auction",
      text: "Please enter a reason for pausing this auction:",
      input: "textarea",
      inputPlaceholder: "Enter reason here...",
      inputAttributes: {
        rows: 4
      },
      showCancelButton: true,
      confirmButtonText: "Pause",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ed6c02",
      cancelButtonColor: "#6c757d",
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return "Please enter a reason";
        }
      }
    });

    if (result.isConfirmed && result.value) {
      try {
        setActionLoading(true);
        const response = await apiAuctions.auctionControl({
          id,
          action: "pause",
          message: result.value.trim()
        });
        
        toast.success("Auction paused successfully");
        
        if (response?.data?.auction) {
          setAuction(response.data.auction);
        }
      } catch (error) {
        errorHandler(error);
        toast.error("Failed to pause auction");
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleResumeAuction = async () => {
    if (!id) return;
  
    const { value: result } = await Swal.fire({
      title: "Resume Auction",
      html: `
        <div style="text-align:left">
          <label style="display:block;margin-bottom:8px">
            <input type="radio" name="resumeOption" value="same" checked />
            Resume with same end date
          </label>
  
          <label style="display:block;margin-bottom:8px">
            <input type="radio" name="resumeOption" value="extend" />
            Extend end date
          </label>
  
          <input
            type="datetime-local"
            id="endDateInput"
            class="swal2-input"
            style="display:none;margin-top:10px"
          />
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Continue",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#1976d2",
      cancelButtonColor: "#6c757d",
      didOpen: () => {
        const radios = document.querySelectorAll('input[name="resumeOption"]');
        const dateInput = document.getElementById("endDateInput");
  
        radios.forEach((radio) => {
          radio.addEventListener("change", () => {
            if (radio.value === "extend" && radio.checked) {
              dateInput.style.display = "block";
  
              const now = new Date();
              now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
              dateInput.min = now.toISOString().slice(0, 16);
            } else if (radio.value === "same" && radio.checked) {
              dateInput.style.display = "none";
              dateInput.value = "";
            }
          });
        });
      },
      preConfirm: () => {
        const selected = document.querySelector(
          'input[name="resumeOption"]:checked'
        )?.value;
  
        const dateInput = document.getElementById("endDateInput");
  
        if (selected === "extend") {
          if (!dateInput.value) {
            Swal.showValidationMessage("Please select end date and time");
            return false;
          }
          return {
            option: "extend",
            endTime: new Date(dateInput.value).toISOString(),
          };
        }
  
        return { option: "same" };
      },
    });
  
    if (!result) return;
  
    try {
      setActionLoading(true);
  
      const response = await apiAuctions.auctionControl({
        id,
        action: "resume",
        message: "Auction is live again",
        ...(result.option === "extend" && { endTime: result.endTime }),
      });
  
      toast.success("Auction resumed successfully");
  
      if (response?.data?.auction) {
        setAuction(response.data.auction);
      }
    } catch (error) {
      errorHandler(error);
      toast.error("Failed to resume auction");
    } finally {
      setActionLoading(false);
    }
  };
  

  const getButtonStates = () => {
    const status = auction?.auctionStatus || "";
    const isCompleted = status === "Completed" || status === "Ended";
    const isLive = status === "Live";
    const isPaused = status === "Paused";
    const isUpcoming = status === "Upcoming";

    // If auction is completed, disable all buttons
    if (isCompleted) {
      return {
        startDisabled: true,
        pauseDisabled: true,
        resumeDisabled: true,
        endDisabled: true,
      };
    }

    return {
      startDisabled: isLive || isPaused || !isUpcoming || actionLoading,
      pauseDisabled: isPaused || isUpcoming || !isLive || actionLoading,
      resumeDisabled: isLive || isUpcoming || !isPaused || actionLoading,
      endDisabled: isUpcoming || (!isLive && !isPaused) || actionLoading,
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <Loader />
      </Box>
    );
  }

  if (error && !auction) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
                                                                                                                        
  const renderLayout = () => {
    const buttonStates = getButtonStates();
    const auctionControlProps = {
      onStart: handleStartAuction,
      onPause: handlePauseAuction,
      onResume: handleResumeAuction,
      buttonStates,
      actionLoading,
    };

    const blockControlProps = {
      selectedUsers,
      blockedUsers,
      handleSelectAll,
      handleSelectUser,
      handleBlockUsers,
      handleUnblockUser,
      actionLoading,
    };

    if (auctionTypeFilter === "marketDropAi") {
      return <MarketDropAiLayout auction={auction} winners={winners} bids={bids} auctionControl={auctionControlProps} blockControl={blockControlProps} />;
    } else if (auctionTypeFilter === "claimPoint") {
      return <ClaimPointLayout auction={auction} winners={winners} bids={bids} priceDrops={priceDrops} auctionControl={auctionControlProps} blockControl={blockControlProps} />;
    } else {
      return <NormalAuctionLayout auction={auction} bids={bids} auctionControl={auctionControlProps} blockControl={blockControlProps} />;
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <BackButton variant="h6" />
      </Box>
      {renderLayout()}
    </Box>
  );
};

// ==================== AUCTION CONTROL BUTTONS COMPONENT ====================
const AuctionControlButtons = ({ auctionControl }) => {
  const { onStart, onPause, onResume, onEnd, buttonStates, actionLoading } = auctionControl;

  return (
    <Card sx={{ mb: 2, boxShadow: 2 }}>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
          Auction Control
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" gap={2}>
          <Button
            variant="contained"
            color="success"
            startIcon={<PlayArrowIcon />}
            onClick={onStart}
            disabled={buttonStates.startDisabled}
            size="large"
          >
            Start Auction
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<PauseIcon />}
            onClick={onPause}
            disabled={buttonStates.pauseDisabled}
            size="large"
          >
            Pause Auction
          </Button>
          <Button
            variant="contained"
            color="info"
            startIcon={<RestartAltIcon />}
            onClick={onResume}
            disabled={buttonStates.resumeDisabled}
            size="large"
          >
            Resume Auction
          </Button>
        </Stack>
        {actionLoading && (
          <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Processing...
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== NORMAL AUCTION LAYOUT ====================
const NormalAuctionLayout = ({ auction, bids, auctionControl, blockControl }) => {
  const theme = useTheme();

  // Orange color scheme to match other layouts
  const orangeMain = "#D45A00";
  const orangeDark = "#B44800";

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
  };

  return (
    <>
      {/* Auction Header - Orange Gradient */}
      <Card sx={{ mb: 2, background: `linear-gradient(135deg, ${orangeDark} 0%, ${orangeMain} 100%)`, color: "white" }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Avatar sx={{ width: 50, height: 50, bgcolor: alpha(theme.palette.common.white, 0.2), border: `3px solid ${alpha(theme.palette.common.white, 0.5)}` }}>
                <DirectionsCarIcon sx={{ fontSize: 50 }} />
              </Avatar>
            </Grid>
            <Grid item xs={12} sm={9} md={10}>
              <Box>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  {`${auction?.vehicle?.make || ""} ${auction?.vehicle?.model || ""}`.trim() || "Auction Details"}
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                  <Chip 
                    label="Normal Auction" 
                    sx={{ 
                      bgcolor: alpha(theme.palette.common.white, 0.15), 
                      color: "white",
                      '& .MuiChip-icon': { color: "white" }
                    }} 
                  />
                  <Chip 
                    label={auction?.auctionStatus} 
                    sx={{ 
                      bgcolor: alpha(theme.palette.common.white, 0.15), 
                      color: "white",
                      '& .MuiChip-icon': { color: "white" }
                    }} 
                  />
                  <Chip 
                    icon={<AttachMoneyIcon />} 
                    label={`Starting Bid: ${formatCurrency(auction?.startingBid || 0)}`} 
                    sx={{ 
                      bgcolor: alpha(theme.palette.common.white, 0.15), 
                      color: "white",
                      '& .MuiChip-icon': { color: "white" }
                    }} 
                  />
                  <Chip 
                    icon={<CalendarTodayIcon />} 
                    label={`Total Bids: ${bids.length}`} 
                    sx={{ 
                      bgcolor: alpha(theme.palette.common.white, 0.15), 
                      color: "white",
                      '& .MuiChip-icon': { color: "white" }
                    }} 
                  />
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Auction Control Buttons */}
      {auctionControl && <AuctionControlButtons auctionControl={auctionControl} />}

      {/* Highest Bidder Section */}
      {bids.length > 0 && (() => {
        const highestBid = bids.reduce((max, bid) => 
          (!max || (bid.amount || 0) > (max.amount || 0)) ? bid : max, 
          null
        );
        return highestBid ? (
          <Card sx={{ mb: 2, boxShadow: 3, border: '2px solid', borderColor: 'primary.main' }}>
            <CardContent>
              <Grid container spacing={3} alignItems="center">
                <Grid item>
                  <Avatar 
                    src={highestBid.bidder?.userImage ? IMAGE_BASE + highestBid.bidder.userImage : undefined}
                    sx={{ width: 80, height: 80, bgcolor: highestBid.bidder?.userImage ? "" : "primary.main", color: "white" }}
                  >
                    <Typography variant="h3">{highestBid.bidder?.name?.charAt(0)?.toUpperCase() || "?"}</Typography>
                  </Avatar>
                </Grid>
                <Grid item xs={12} sm={9} md={10}>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Chip 
                        label="HIGHEST BIDDER" 
                        color="primary" 
                        size="small"
                        sx={{ fontWeight: "bold" }}
                      />
                    </Box>
                    <Typography variant="h5" fontWeight="bold" color="text.primary" gutterBottom>
                      {highestBid.bidder?.name || "Unknown"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {highestBid.bidder?.email || ""}
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                      <Chip 
                        icon={<AttachMoneyIcon />} 
                        label={`Highest Bid: ${formatCurrency(highestBid.amount)}`} 
                        color="primary"
                        sx={{ fontWeight: "bold" }}
                      />
                      <Chip 
                        icon={<CalendarTodayIcon />} 
                        label={`Bid Time: ${formatDateTime(highestBid.createdAt)}`} 
                        variant="outlined"
                      />
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ) : null;
      })()}

      {/* Bid History Section */}
      <Card sx={{ boxShadow: 3 }}>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <Typography variant="h5" fontWeight="bold" color="text.primary">Bid History</Typography>
              {blockControl && blockControl.selectedUsers.length > 0 && (
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<BlockIcon />}
                  onClick={() => blockControl.handleBlockUsers(blockControl.selectedUsers)}
                  disabled={blockControl.actionLoading}
                >
                  Block Selected ({blockControl.selectedUsers.length})
                </Button>
              )}
            </Box>
          }
          sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}
        />
        <CardContent>
          {bids.length === 0 ? (
            <Alert severity="info">No bids found for this auction.</Alert>
          ) : (
            <TableContainer 
              component={Paper} 
              sx={{ 
                borderRadius: 2,
                maxHeight: '600px',
                overflow: 'auto',
                boxShadow: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell padding="checkbox" sx={{ fontWeight: "bold", color: "text.primary", fontSize: '0.875rem', py: 2, width: '60px' }}>
                      {blockControl && (
                        <Checkbox
                          indeterminate={blockControl.selectedUsers.length > 0 && blockControl.selectedUsers.length < bids.filter(b => b.bidder?._id && !blockControl.blockedUsers.includes(b.bidder._id)).length}
                          checked={bids.length > 0 && blockControl.selectedUsers.length === bids.filter(b => b.bidder?._id && !blockControl.blockedUsers.includes(b.bidder._id)).length && bids.filter(b => b.bidder?._id && !blockControl.blockedUsers.includes(b.bidder._id)).length > 0}
                          onChange={blockControl.handleSelectAll}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "text.primary", fontSize: '0.875rem', py: 2 }}>
                      BID VALUE
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "text.primary", fontSize: '0.875rem', py: 2 }}>
                      USER
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "text.primary", fontSize: '0.875rem', py: 2 }} align="right">
                      LAST BID TIME
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "text.primary", fontSize: '0.875rem', py: 2, width: '80px' }} align="center">
                      ACTION
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const highestBid = bids.length > 0 ? bids.reduce((max, bid) => 
                      (!max || (bid.amount || 0) > (max.amount || 0)) ? bid : max, 
                      null
                    ) : null;
                    return bids.map((bid, index) => {
                      const isHighestBidder = highestBid && bid._id === highestBid._id;
                      const userId = bid.bidder?._id;
                      const isBlocked = blockControl && userId && blockControl.blockedUsers.includes(userId);
                      const isSelected = blockControl && userId && blockControl.selectedUsers.includes(userId);
                      return (
                        <TableRow 
                          key={bid._id || index} 
                          hover
                          sx={{
                            bgcolor: isHighestBidder ? 'primary.50' : isBlocked ? 'error.50' : 'inherit',
                            borderLeft: isHighestBidder ? '4px solid' : 'none',
                            borderColor: isHighestBidder ? 'primary.main' : 'transparent',
                            opacity: isBlocked ? 0.7 : 1,
                            '&:nth-of-type(even)': {
                              bgcolor: isHighestBidder ? 'primary.50' : isBlocked ? 'error.50' : 'grey.50'
                            },
                            '&:hover': {
                              bgcolor: isHighestBidder ? 'primary.100' : 'action.hover'
                            }
                          }}
                        >
                      <TableCell padding="checkbox" sx={{ py: 2 }}>
                        {blockControl && userId && !isBlocked && (
                          <Checkbox
                            checked={isSelected}
                            onChange={() => blockControl.handleSelectUser(userId)}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body1" fontWeight="bold" color="text.primary">
                          {formatCurrency(bid.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar 
                            src={bid.bidder?.userImage ? IMAGE_BASE + bid.bidder.userImage : undefined}
                            sx={{ width: 40, height: 40, bgcolor: isHighestBidder && !bid.bidder?.userImage ? "primary.main" : isHighestBidder && bid.bidder?.userImage ? "" : bid.bidder?.userImage ? "" : "grey.400", color: "white" }}
                          >
                            {bid.bidder?.name?.charAt(0)?.toUpperCase() || "?"}
                          </Avatar>
                          <Box>
                            <Typography variant="body1" fontWeight={isHighestBidder ? "bold" : "medium"} color="text.primary">
                              {bid.bidder?.name || "Unknown"}
                              {isHighestBidder && (
                                <Chip 
                                  label="Highest" 
                                  size="small" 
                                  color="primary"
                                  sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
                                />
                              )}
                              {isBlocked && (
                                <Chip 
                                  label="Blocked" 
                                  size="small" 
                                  color="error"
                                  sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
                                />
                              )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.25 }}>
                              {bid.bidder?.email || ""}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2 }}>
                        <Typography variant="body2" color="text.primary">
                          {formatDateTime(bid.createdAt)}
                          </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2 }}>
                        {blockControl && userId && (
                          isBlocked ? (
                            <Tooltip title="Unblock User">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => blockControl.handleUnblockUser(userId)}
                                disabled={blockControl.actionLoading}
                              >
                                <UnblockIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Block User">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => blockControl.handleBlockUsers([userId])}
                                disabled={blockControl.actionLoading}
                              >
                                <BlockIcon />
                              </IconButton>
                            </Tooltip>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </>
  );
};

// ==================== MARKETDROP AI LAYOUT ====================
const MarketDropAiLayout = ({ auction, winners, bids, auctionControl }) => {
  const theme = useTheme();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount || 0);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
  };

  const displayWinner = winners.length > 0 ? winners[0] : null;
  const displayBids = bids;
  const orangeMain = "#D45A00";
  const orangeDark = "#B44800";

  return (
    <>
      {/* Auction Header - Orange Gradient */}
      <Card sx={{ mb: 3, background: `linear-gradient(135deg, ${orangeDark} 0%, ${orangeMain} 100%)`, color: "white" }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Avatar sx={{ width: 100, height: 100, bgcolor: alpha(theme.palette.common.white, 0.2), border: `3px solid ${alpha(theme.palette.common.white, 0.5)}` }}>
                <GavelIcon sx={{ fontSize: 50 }} />
              </Avatar>
            </Grid>
            <Grid item xs={12} sm={9} md={10}>
              <Box>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  { `${auction?.vehicle?.make || ""} ${auction?.vehicle?.model || ""}`.trim() || "Auction Details"}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                  MarketDropAi - Bids & Winners
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                  <Chip 
                    icon={<AttachMoneyIcon />} 
                    label={`Total Auto Claim Users: ${displayBids.length}`} 
                    sx={{ 
                      bgcolor: alpha(theme.palette.common.white, 0.15), 
                      color: "white",
                      '& .MuiChip-icon': { color: "white" }
                    }} 
                  />
                  {auction?.schedule?.startTime && (
                    <Chip 
                      icon={<CalendarTodayIcon />} 
                      label={`Start: ${formatDateTime(auction.schedule.startTime)}`} 
                      sx={{ 
                        bgcolor: alpha(theme.palette.common.white, 0.15), 
                        color: "white",
                        '& .MuiChip-icon': { color: "white" }
                      }} 
                    />
                  )}
                  {auction?.schedule?.endTime && (
                    <Chip 
                      icon={<CalendarTodayIcon />} 
                      label={`End: ${formatDateTime(auction.schedule.endTime)}`} 
                      sx={{ 
                        bgcolor: alpha(theme.palette.common.white, 0.15), 
                        color: "white",
                        '& .MuiChip-icon': { color: "white" }
                      }} 
                    />
                  )}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Auction Control Buttons */}
      {auctionControl && <AuctionControlButtons auctionControl={auctionControl} />}

      {/* Winner and History Side by Side */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Winner Column */}
        <Grid item xs={12}  className="bid-control-card-container-winners">
          {displayWinner ? (
            <Card className="bid-control-card">
              <CardHeader
                title={
                  <Box className="bid-control-section-title">
                    <CheckCircleIcon className="bid-control-header-icon" />
                    <Typography variant="h5" fontWeight="bold" color="text.primary">Winner</Typography>
                  </Box>
                }
                className="bid-control-card-header"
              />
              <CardContent className="bid-control-card-content">
                <Card
                  elevation={0}
                  className="bid-control-winner-inner-card"
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box className="bid-control-winner-avatar-container">
                      <Avatar 
                        src={displayWinner.bidder?.userImage ? IMAGE_BASE + displayWinner.bidder.userImage : undefined}
                        className={displayWinner.bidder?.userImage ? "bid-control-winner-avatar-default" : "bid-control-winner-avatar"}
                      >
                        {!displayWinner.bidder?.userImage && (
                          <Typography variant="h3">{displayWinner.bidder?.name?.charAt(0)?.toUpperCase() || "?"}</Typography>
                        )}
                      </Avatar>
                      <Chip 
                        label="WINNER" 
                        className="bid-control-winner-chip"
                        icon={<CheckCircleIcon sx={{ color: "white", fontSize: 16 }} />} 
                      />
                    </Box>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" className="bid-control-label-text">
                          Winner Name
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" color="text.primary" className="bid-control-winner-name">
                          {displayWinner.bidder?.name || "Unknown"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" className="bid-control-email-text">
                          {displayWinner.bidder?.email || "N/A"}
                        </Typography>
                      </Box>
                      <Divider sx={{ bgcolor: alpha(orangeMain, 0.2) }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary" className="bid-control-label-text">
                          Claim Amount
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" className="bid-control-winning-bid">
                          {formatCurrency(displayWinner.amount)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} className="bid-control-label-text">
                          Deposit
                        </Typography>
                        {displayWinner.depositVerified ? (
                          <Chip 
                            label="Verified" 
                            size="small" 
                            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} 
                            className="bid-control-verified-chip"
                          />
                        ) : (
                          <Chip 
                            label="Pending" 
                            color="warning" 
                            size="small" 
                            icon={<WarningIcon sx={{ fontSize: 14 }} />} 
                            sx={{ fontWeight: 500, height: 24 }} 
                          />
                        )}
                      </Box>
                      {displayWinner.bidder?.phone && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" className="bid-control-label-text">
                            Phone
                          </Typography>
                          <Typography variant="body2" color="text.primary" className="bid-control-icon-text">
                            <PhoneIcon sx={{ fontSize: 14, color: orangeMain }} />
                            {displayWinner.bidder.phone}
                          </Typography>
                        </Box>
                      )}
                      <Box>
                        <Typography variant="body2" color="text.secondary" className="bid-control-label-text">
                          Claim Time
                        </Typography>
                        <Typography variant="body2" color="text.primary" className="bid-control-icon-text">
                          <CalendarTodayIcon sx={{ fontSize: 14, color: orangeMain }} />
                          {formatDateTime(displayWinner.createdAt)}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          ) : (
            <Card className="bid-control-card">
              <CardHeader
                title={
                  <Box className="bid-control-section-title">
                    <CheckCircleIcon className="bid-control-header-icon" />
                    <Typography variant="h5" fontWeight="bold" color="text.primary">Winner</Typography>
                  </Box>
                }
                className="bid-control-card-header"
              />
              <CardContent>
                {auction?.auctionStatus === "Completed" ? (
                  <Alert severity="success" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                    Congratulations! The system has claimed it.
                  </Alert>
                ) : (
                  <Alert severity="info">No winner yet.</Alert>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Bid History Column */}
        <Grid item xs={12} className="bid-control-card-container-history">
          <Card className="bid-control-card" sx={{ display: 'flex', flexDirection: 'column' }}>
            <CardHeader
              title={
                <Box className="bid-control-section-title">
                  <AttachMoneyIcon className="bid-control-header-icon" />
                  <Typography variant="h5" fontWeight="bold" color="text.primary">Users (Set Auto Claim)</Typography>
                </Box>
              }
              className="bid-control-card-header"
              sx={{ bgcolor: 'background.paper' }}
            />
            <CardContent className="bid-control-card-content" sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {displayBids.length === 0 ? (
                <Alert severity="info">No users found for this auction.</Alert>
              ) : (
                <TableContainer 
                  component={Paper} 
                  className="bid-control-table-container"
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow className="bid-control-table-header">
                        <TableCell className="bid-control-table-header-cell">
                          Auto Claim VALUE
                        </TableCell>
                        <TableCell className="bid-control-table-header-cell">
                          USER
                        </TableCell>
                        <TableCell className="bid-control-table-header-cell" align="right">
                          TIME
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayBids.map((bid, index) => {
                        const isWinner = displayWinner && bid._id === displayWinner._id;
                        return (
                          <TableRow 
                            key={bid._id || index} 
                            hover
                            className={isWinner ? "bid-control-table-row-winner" : ""}
                            sx={{
                              '&:nth-of-type(even)': {
                                bgcolor: isWinner ? alpha(orangeMain, 0.12) : "grey.50"
                              },
                              '&:hover': {
                                bgcolor: isWinner ? alpha(orangeMain, 0.2) : "action.hover"
                              }
                            }}
                          >
                            <TableCell className="bid-control-table-cell">
                              <Typography variant="body1" fontWeight="bold" color="text.primary" className="bid-control-bid-value-text">
                                {formatCurrency(bid.amount)}
                              </Typography>
                            </TableCell>
                            <TableCell className="bid-control-table-cell">
                              <Box className="bid-control-user-cell">
                                <Avatar 
                                  src={bid.bidder?.userImage ? IMAGE_BASE + bid.bidder.userImage : undefined}
                                  className={bid.bidder?.userImage ? "bid-control-user-avatar-default" : "bid-control-user-avatar"}
                                  // sx={{ bgcolor: isWinner ? orangeMain : "primary.main" }}
                                >
                                  {bid.bidder?.name?.charAt(0)?.toUpperCase() || "?"}
                                </Avatar>
                                <Box>
                                  <Typography 
                                    variant="body1" 
                                    fontWeight={isWinner ? "bold" : "medium"} 
                                    color="text.primary"
                                    className="bid-control-user-name"
                                  >
                                    {bid.bidder?.name || "Unknown"}
                                    {isWinner && (
                                      <Chip 
                                        label="Winner" 
                                        size="small" 
                                        className="bid-control-winner-chip-small"
                                      />
                                    )}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" className="bid-control-email-text" sx={{ mt: 0.25 }}>
                                    {bid.bidder?.email || ""}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="right" className="bid-control-table-cell">
                              <Typography variant="body2" color="text.primary" className="bid-control-info-text">
                                {formatDateTime(bid.createdAt)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

// ==================== CLAIMPOINT LAYOUT ====================
const ClaimPointLayout = ({ auction, winners, bids, priceDrops, auctionControl }) => {
  const theme = useTheme();  

  const orangeMain = "#D45A00";
  const orangeDark = "#B44800";

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount || 0);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
  };

  return (
    <>
      {/* Auction Header - Orange Gradient */}
      <Card sx={{ mb: 3, background: `linear-gradient(135deg, ${orangeDark} 0%, ${orangeMain} 100%)`, color: "white" }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm="auto">
              <Avatar sx={{ 
                width: { xs: 60, sm: 80, md: 100 }, 
                height: { xs: 60, sm: 80, md: 100 }, 
                bgcolor: alpha(theme.palette.common.white, 0.2), 
                border: `3px solid ${alpha(theme.palette.common.white, 0.5)}` 
              }}>
                <GavelIcon sx={{ fontSize: { xs: 30, sm: 40, md: 50 } }} />
              </Avatar>
            </Grid>
            <Grid item xs={12} sm>
              <Box>
                <Typography 
                  variant="h4" 
                  fontWeight="bold" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
                >
                  {`${auction?.vehicle?.year || ""} ${capitalizeFirstLetter(auction?.vehicle?.make || "")} ${capitalizeFirstLetter(auction?.vehicle?.model || "")}`.trim() || "Auction Details"}
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    opacity: 0.9, 
                    mb: 2,
                    fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }
                  }}
                >
                  ClaimPoint - Bids & Winners
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                  {console.log(auction,'auction')}
                  <Chip 
                    icon={<AttachMoneyIcon />} 
                    label={`Starting Bid: ${formatCurrency(auction?.startingBid || 0)}`} 
                    size="small"
                    sx={{ 
                      bgcolor: alpha(theme.palette.common.white, 0.15), 
                      color: "white",
                      '& .MuiChip-icon': { color: "white" },
                      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }
                    }} 
                  />
                  <Chip 
                    icon={<CalendarTodayIcon />} 
                    label={`Total Bids: ${bids.length}`} 
                    size="small"
                    sx={{ 
                      bgcolor: alpha(theme.palette.common.white, 0.15), 
                      color: "white",
                      '& .MuiChip-icon': { color: "white" },
                      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }
                    }} 
                  />
                  <Chip 
                    icon={<CheckCircleIcon />} 
                    label={`Auction Status: ${auction?.auctionStatus}`} 
                    size="small"
                    sx={{ 
                      bgcolor: alpha(theme.palette.common.white, 0.15), 
                      color: "white",
                      '& .MuiChip-icon': { color: "white" },
                      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }
                    }} 
                  />
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Auction Control Buttons */}
      {auctionControl && <AuctionControlButtons auctionControl={auctionControl} />}

      {/* Winner and History Side by Side */}
      <Grid container spacing={{ xs: 2, sm: 2, md: 3 }} sx={{ mb: 3 }}>
        {/* Winner Column */}
        <Grid item xs={12} sm={12} md={12} lg={2} className="bid-control-card-container-winner">
          {winners.length > 0 ? (
            <Card className="bid-control-card">
              <CardHeader
                title={
                  <Box className="bid-control-section-title">
                    <CheckCircleIcon className="bid-control-header-icon" />
                    <Typography variant="h5" fontWeight="bold" color="text.primary">Winner</Typography>
                  </Box>
                }
                className="bid-control-card-header"
              />
              <CardContent className="bid-control-card-content">
                {winners.map((winner, index) => (
                  <Card
                    key={winner._id || index}
                    elevation={0}
                    className="bid-control-winner-inner-card"
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
                      <Box className="bid-control-winner-avatar-container">
                        <Avatar 
                          src={winner.bidder?.userImage ? IMAGE_BASE + winner.bidder.userImage : undefined}
                          className={winner.bidder?.userImage ? "bid-control-winner-avatar-default" : "bid-control-winner-avatar"}
                          sx={{
                            width: { xs: 60, sm: 80, md: 100 },
                            height: { xs: 60, sm: 80, md: 100 }
                          }}
                        >
                          {!winner.bidder?.userImage && (
                            <Typography 
                              variant="h3"
                              sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' } }}
                            >
                              {winner.bidder?.name?.charAt(0)?.toUpperCase() || "?"}
                            </Typography>
                          )}
                        </Avatar>
                        <Chip 
                          label="WINNER" 
                          className="bid-control-winner-chip"
                          size="small"
                          icon={<CheckCircleIcon sx={{ color: "white", fontSize: { xs: 12, sm: 14, md: 16 } }} />} 
                          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' } }}
                        />
                      </Box>
                      <Stack spacing={{ xs: 1.5, sm: 2, md: 2 }}>
                        <Box>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            className="bid-control-label-text"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}
                          >
                            Winner Name
                          </Typography>
                          <Typography 
                            variant="h6" 
                            fontWeight="bold" 
                            color="text.primary" 
                            className="bid-control-winner-name"
                            sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }}
                          >
                            {winner.bidder?.name || "Unknown"}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            className="bid-control-email-text"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}
                          >
                            {winner.bidder?.email || "N/A"}
                          </Typography>
                        </Box>
                        <Divider sx={{ bgcolor: alpha(orangeMain, 0.2) }} />
                        <Box>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            className="bid-control-label-text"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}
                          >
                            Winning Bid
                          </Typography>
                          <Typography 
                            variant="h5" 
                            fontWeight="bold" 
                            className="bid-control-winning-bid"
                            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}
                          >
                            {formatCurrency(winner.amount)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            className="bid-control-label-text"
                            sx={{ 
                              mb: 1,
                              fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } 
                            }}
                          >
                            Deposit
                          </Typography>
                          {winner.depositVerified ? (
                            <Chip 
                              label="Verified" 
                              size="small" 
                              icon={<CheckCircleIcon sx={{ fontSize: { xs: 12, sm: 14, md: 14 } }} />} 
                              className="bid-control-verified-chip"
                              sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' } }}
                            />
                          ) : (
                            <Chip 
                              label="Pending" 
                              color="warning" 
                              size="small" 
                              icon={<WarningIcon sx={{ fontSize: { xs: 12, sm: 14, md: 14 } }} />} 
                              sx={{ 
                                fontWeight: 500, 
                                height: { xs: 20, sm: 24, md: 24 },
                                fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' }
                              }} 
                            />
                          )}
                        </Box>
                        {winner.bidder?.phone && (
                          <Box>
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              className="bid-control-label-text"
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}
                            >
                              Phone
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="text.primary" 
                              className="bid-control-icon-text"
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}
                            >
                              <PhoneIcon sx={{ fontSize: { xs: 12, sm: 14, md: 14 }, color: orangeMain }} />
                              {winner.bidder.phone}
                            </Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            className="bid-control-label-text"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}
                          >
                            Bid Time
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.primary" 
                            className="bid-control-icon-text"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}
                          >
                            <CalendarTodayIcon sx={{ fontSize: { xs: 12, sm: 14, md: 14 }, color: orangeMain }} />
                            {fDateTime(winner.createdAt)}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>
          ) : (
            <Card className="bid-control-card">
              <CardHeader
                title={
                  <Box className="bid-control-section-title">
                    <CheckCircleIcon className="bid-control-header-icon" />
                    <Typography variant="h5" fontWeight="bold" color="text.primary">Winner</Typography>
                  </Box>
                }
                className="bid-control-card-header"
              />
              <CardContent>
                <Alert severity="info">Auction is not over yet.</Alert>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Bid History Column */}
        <Grid item xs={12} sm={12} md={6} lg={5} className="bid-control-card-container">
          <Card className="bid-control-card" sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <CardHeader
              title={
                <Box className="bid-control-section-title">
                  <AttachMoneyIcon className="bid-control-header-icon" />
                  <Typography variant="h5" fontWeight="bold" color="text.primary">Bid History</Typography>
                </Box>
              }
              className="bid-control-card-header"
              sx={{ bgcolor: 'background.paper' }}
            />
            <CardContent className="bid-control-card-content" sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {bids.length === 0 ? (
                <Alert severity="info">No bids found for this auction.</Alert>
              ) : (
                <TableContainer 
                  component={Paper} 
                  className="bid-control-table-container"
                  sx={{ 
                    maxHeight: { xs: '400px', sm: '500px', md: '600px' },
                    overflowX: 'auto',
                    overflowY: 'auto',
                    width: '100%',
                    // Custom Scrollbar Styling - Webkit (Chrome, Safari, Edge)
                    '&::-webkit-scrollbar': {
                      width: '8px',
                      height: '8px'
                    },
                    '&::-webkit-scrollbar-track': {
                      background: alpha(orangeMain, 0.1),
                      borderRadius: '10px',
                      margin: '4px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: `linear-gradient(135deg, ${orangeMain} 0%, ${orangeDark} 100%)`,
                      borderRadius: '10px',
                      border: `2px solid ${alpha(theme.palette.common.white, 0.2)}`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${orangeDark} 0%, ${orangeMain} 100%)`
                      }
                    },
                    '&::-webkit-scrollbar-corner': {
                      background: alpha(orangeMain, 0.1)
                    },
                    // Firefox Scrollbar
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${orangeMain} ${alpha(orangeMain, 0.1)}`
                  }}
                >
                  <Table stickyHeader size="small" sx={{ width: '100%' }}>
                    <TableHead>
                      <TableRow className="bid-control-table-header">
                        <TableCell 
                          className="bid-control-table-header-cell"
                          sx={{ 
                            fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                            py: { xs: 1, sm: 1.5, md: 2 },
                            whiteSpace: 'nowrap'
                          }}
                        >
                          BID VALUE
                        </TableCell>
                        <TableCell 
                          className="bid-control-table-header-cell"
                          sx={{ 
                            fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                            py: { xs: 1, sm: 1.5, md: 2 },
                            whiteSpace: 'nowrap'
                          }}
                        >
                          USER
                        </TableCell>
                        <TableCell 
                          className="bid-control-table-header-cell" 
                          align="right"
                          sx={{ 
                            fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                            py: { xs: 1, sm: 1.5, md: 2 },
                            whiteSpace: 'nowrap'
                          }}
                        >
                          LAST BID TIME
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bids.map((bid, index) => (
                        <TableRow
                          key={bid._id || index}
                          hover
                          className={bid.isWinner ? "bid-control-table-row-winner" : ""}
                          sx={{
                            bgcolor: bid.isWinner ? alpha(orangeMain, 0.08) : "transparent",
                            transition: 'background-color 0.2s ease-in-out',
                            '&:nth-of-type(even)': {
                              bgcolor: bid.isWinner ? alpha(orangeMain, 0.12) : "grey.50"
                            },
                            "&:hover": { 
                              bgcolor: bid.isWinner ? alpha(orangeMain, 0.2) : "action.hover",
                              transition: 'background-color 0.2s ease-in-out'
                            }
                          }}
                        >
                          <TableCell 
                            className="bid-control-table-cell"
                            sx={{ 
                              py: { xs: 1, sm: 1.5, md: 2 },
                              whiteSpace: 'nowrap',
                              minWidth: { xs: '100px', sm: '120px', md: '150px' }
                            }}
                          >
                            <Typography 
                              variant="body1" 
                              fontWeight="bold" 
                              color="text.primary" 
                              className="bid-control-bid-value-text"
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}
                            >
                              {formatCurrency(bid.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell 
                            className="bid-control-table-cell"
                            sx={{ 
                              py: { xs: 1, sm: 1.5, md: 2 },
                              minWidth: { xs: '150px', sm: '200px', md: '250px' }
                            }}
                          >
                            <Box className="bid-control-user-cell">
                              <Avatar 
                                src={bid.bidder?.userImage ? IMAGE_BASE + bid.bidder.userImage : undefined}
                                className="bid-control-user-avatar"
                                sx={{ 
                                  bgcolor: bid.isWinner ? orangeMain : "primary.main",
                                  width: { xs: 32, sm: 40, md: 40 },
                                  height: { xs: 32, sm: 40, md: 40 },
                                  fontSize: { xs: '0.875rem', sm: '1rem', md: '1rem' }
                                }}
                              >
                                {bid.bidder?.name?.charAt(0)?.toUpperCase() || "?"}
                              </Avatar>
                              <Box>
                                <Typography 
                                  variant="body1" 
                                  fontWeight="medium" 
                                  color="text.primary" 
                                  className="bid-control-user-name"
                                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}
                                >
                                  {bid.bidder?.name || "Unknown"}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  className="bid-control-email-text" 
                                  sx={{ 
                                    mt: 0.25,
                                    fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' },
                                    display: { xs: 'none', sm: 'block' }
                                  }}
                                >
                                  {bid.bidder?.email || ""}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell 
                            align="right" 
                            className="bid-control-table-cell"
                            sx={{ 
                              py: { xs: 1, sm: 1.5, md: 2 },
                              whiteSpace: 'nowrap',
                              minWidth: { xs: '120px', sm: '150px', md: '180px' }
                            }}
                          >
                            <Typography 
                              variant="body2" 
                              color="text.primary" 
                              className="bid-control-info-text"
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}
                            >
                              {formatDateTime(bid.createdAt)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Price Drop History Column */}
        <Grid item xs={12} sm={12} md={6} lg={5} className="bid-control-card-container">
          <Card className="bid-control-card" sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <CardHeader
              title={
                <Box className="bid-control-section-title">
                  <AttachMoneyIcon className="bid-control-header-icon" />
                  <Typography variant="h5" fontWeight="bold" color="text.primary">Price Drop History</Typography>
                </Box>
              }
              className="bid-control-card-header"
              sx={{ bgcolor: 'background.paper' }}
            />
            <CardContent className="bid-control-card-content" sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {priceDrops.length === 0 ? (
                <Alert severity="info">No price Drop found for this auction.</Alert>
              ) : (
                <TableContainer 
                  component={Paper} 
                  className="bid-control-table-container"
                  sx={{ 
                    maxHeight: { xs: '400px', sm: '500px', md: '600px' },
                    overflowX: 'auto',
                    overflowY: 'auto',
                    width: '100%',
                    // Custom Scrollbar Styling - Webkit (Chrome, Safari, Edge)
                    '&::-webkit-scrollbar': {
                      width: '8px',
                      height: '8px'
                    },
                    '&::-webkit-scrollbar-track': {
                      background: alpha(orangeMain, 0.1),
                      borderRadius: '10px',
                      margin: '4px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: `linear-gradient(135deg, ${orangeMain} 0%, ${orangeDark} 100%)`,
                      borderRadius: '10px',
                      border: `2px solid ${alpha(theme.palette.common.white, 0.2)}`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${orangeDark} 0%, ${orangeMain} 100%)`
                      }
                    },
                    '&::-webkit-scrollbar-corner': {
                      background: alpha(orangeMain, 0.1)
                    },
                    // Firefox Scrollbar
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${orangeMain} ${alpha(orangeMain, 0.1)}`
                  }}
                >
                  <Table stickyHeader size="small" sx={{ width: '100%' }}>
                    <TableHead>
                      <TableRow className="bid-control-table-header">
                        <TableCell 
                          className="bid-control-table-header-cell"
                          sx={{ 
                            fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                            py: { xs: 1, sm: 1.5, md: 2 },
                            whiteSpace: 'nowrap'
                          }}
                        >
                          BID VALUE
                        </TableCell>
                        <TableCell 
                          className="bid-control-table-header-cell"
                          sx={{ 
                            fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                            py: { xs: 1, sm: 1.5, md: 2 },
                            whiteSpace: 'nowrap'
                          }}
                        >
                          USER
                        </TableCell>
                        <TableCell 
                          className="bid-control-table-header-cell" 
                          align="right"
                          sx={{ 
                            fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                            py: { xs: 1, sm: 1.5, md: 2 },
                            whiteSpace: 'nowrap'
                          }}
                        >
                          BID TIME
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {priceDrops.map((drop, index) => (
                        <TableRow
                          key={drop._id || index}
                          hover
                          sx={{
                            bgcolor: "transparent",
                            transition: 'background-color 0.2s ease-in-out',
                            '&:nth-of-type(even)': {
                              bgcolor: "grey.50"
                            },
                            "&:hover": { 
                              bgcolor: "action.hover",
                              transition: 'background-color 0.2s ease-in-out'
                            }
                          }}
                        >
                          <TableCell 
                            className="bid-control-table-cell"
                            sx={{ 
                              py: { xs: 1, sm: 1.5, md: 2 },
                              whiteSpace: 'nowrap',
                              minWidth: { xs: '100px', sm: '120px', md: '150px' }
                            }}
                          >
                            <Typography 
                              variant="body1" 
                              fontWeight="bold" 
                              color="text.primary" 
                              className="bid-control-bid-value-text"
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}
                            >
                              {formatCurrency(drop.maxClaimPrice || drop.amount || drop.price || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell 
                            className="bid-control-table-cell"
                            sx={{ 
                              py: { xs: 1, sm: 1.5, md: 2 },
                              minWidth: { xs: '150px', sm: '200px', md: '250px' }
                            }}
                          >
                            <Box className="bid-control-user-cell">
                              <Avatar 
                                src={drop.bidder?.userImage ? IMAGE_BASE + drop.bidder.userImage : undefined}
                                className="bid-control-user-avatar"
                                sx={{ 
                                  bgcolor: drop.bidder?.userImage ? "" : "primary.main",
                                  width: { xs: 32, sm: 40, md: 40 },
                                  height: { xs: 32, sm: 40, md: 40 },
                                  fontSize: { xs: '0.875rem', sm: '1rem', md: '1rem' }
                                }}
                              >
                                {drop.bidder?.name?.charAt(0)?.toUpperCase() || "?"}
                              </Avatar>
                              <Box>
                                <Typography 
                                  variant="body1" 
                                  fontWeight="medium" 
                                  color="text.primary" 
                                  className="bid-control-user-name"
                                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}
                                >
                                  {drop.bidder?.name || "Unknown"}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  className="bid-control-email-text" 
                                  sx={{ 
                                    mt: 0.25,
                                    fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' },
                                    display: { xs: 'none', sm: 'block' }
                                  }}
                                >
                                  {drop.bidder?.email || ""}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell 
                            align="right" 
                            className="bid-control-table-cell"
                            sx={{ 
                              py: { xs: 1, sm: 1.5, md: 2 },
                              whiteSpace: 'nowrap',
                              minWidth: { xs: '120px', sm: '150px', md: '180px' }
                            }}
                          >
                            <Typography 
                              variant="body2" 
                              color="text.primary" 
                              className="bid-control-info-text"
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}
                            >
                              {formatDateTime(drop.placedAt || drop.createdAt || drop.timestamp)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default ViewBidControl;